import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import type { MinimalCard } from '@/services/cardDatabase';

import { pickRecommendationAtom } from '../state/draftAtoms';
import PackCard from './PackCard';

type SortMode = 'default' | 'color' | 'cmc' | 'rarity';

interface PackViewProps {
  cards: MinimalCard[];
  selectedCards: MinimalCard[];
  cardsPerPick: number;
  onSelect: (card: MinimalCard) => void;
  onDoublePick: (card: MinimalCard) => void;
  onConfirm: () => void;
}

const COLOR_ORDER: Record<string, number> = { W: 0, U: 1, B: 2, R: 3, G: 4 };
const RARITY_ORDER: Record<string, number> = { mythic: 0, rare: 1, uncommon: 2, common: 3 };

function getColorSortKey(card: MinimalCard): number {
  if (card.color_identity.length === 0) return 6; // Colorless last
  if (card.color_identity.length > 1) return 5; // Multi after mono
  return COLOR_ORDER[card.color_identity[0]] ?? 6;
}

function sortCards(cards: MinimalCard[], mode: SortMode): MinimalCard[] {
  if (mode === 'default') return cards;
  const sorted = [...cards];

  switch (mode) {
    case 'color':
      sorted.sort((a, b) => getColorSortKey(a) - getColorSortKey(b));
      break;
    case 'cmc':
      sorted.sort((a, b) => (a.cmc ?? 0) - (b.cmc ?? 0));
      break;
    case 'rarity':
      sorted.sort((a, b) => (RARITY_ORDER[a.rarity] ?? 4) - (RARITY_ORDER[b.rarity] ?? 4));
      break;
  }

  return sorted;
}

export default function PackView({ cards, selectedCards, cardsPerPick, onSelect, onDoublePick, onConfirm }: PackViewProps): ReactElement {
  const [sortMode, setSortMode] = useState<SortMode>('default');
  const [focusIndex, setFocusIndex] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const recommendations = useAtomValue(pickRecommendationAtom);

  const sortedCards = sortCards(cards, sortMode);
  const selectedIds = new Set(selectedCards.map((c) => c.id));
  const canConfirm = selectedCards.length >= cardsPerPick || (selectedCards.length > 0 && cards.length < cardsPerPick);

  // Build a map of recommended card IDs → rank (1-based)
  const recommendedMap = new Map<string, number>();
  for (let i = 0; i < recommendations.length; i++) {
    recommendedMap.set(recommendations[i].card.id, i + 1);
  }

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setFocusIndex((i) => Math.min(i + 1, sortedCards.length - 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setFocusIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIndex((i) => Math.min(i + 5, sortedCards.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIndex((i) => Math.max(i - 5, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (canConfirm) {
          onConfirm();
        } else if (sortedCards[focusIndex]) {
          onSelect(sortedCards[focusIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // Deselect handled by parent
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sortedCards, focusIndex, canConfirm, onConfirm, onSelect]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden px-4 pt-4 pb-2">
      {/* Sort controls */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-1">
          {(['default', 'color', 'cmc', 'rarity'] as SortMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => setSortMode(mode)}
              className={clsx(
                'rounded px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                sortMode === mode ? 'bg-zinc-600 text-zinc-100' : 'text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-200',
              )}
            >
              {mode}
            </button>
          ))}
        </div>

        <button
          onClick={onConfirm}
          disabled={!canConfirm}
          className="rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {cardsPerPick > 1 ? `Pick ${selectedCards.length}/${cardsPerPick}` : 'Confirm Pick'}
        </button>
      </div>

      {/* Cards grid */}
      <div
        ref={gridRef}
        className="grid flex-1 content-start justify-items-center gap-x-2 gap-y-4 overflow-y-auto px-1 pt-2 pb-2"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(244px, 100%), 1fr))', maxWidth: '1900px' }}
      >
        {sortedCards.map((card, index) => (
          <PackCard
            key={card.id}
            card={card}
            isSelected={selectedIds.has(card.id)}
            isFocused={index === focusIndex}
            isRecommended={recommendedMap.has(card.id)}
            onClick={() => onSelect(card)}
            onDoubleClick={() => onDoublePick(card)}
          />
        ))}
      </div>
    </div>
  );
}
